/**
 * Login page — kicks off Google SSO. Mirrors the Receipts login pattern: the
 * "Sign in with Google" action is a full-page navigation to the server's OAuth
 * initiator (/api/auth/google), so the OAuth redirect/callback dance stays
 * server-driven and the cookie is set on the same origin.
 *
 * Markup/design ported verbatim from the approved mockups/01-login.html
 * (split brand panel + form panel, design system spec §12).
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import './Login.css';

// Map server error codes (passed back as ?error=) to friendly copy.
const ERROR_MESSAGES = {
  unauthorized_domain: 'Only @frontporchsolutions.com accounts can sign in.',
  invalid_state: 'Your sign-in session expired. Please try again.',
  session_expired: 'Your sign-in session expired. Please try again.',
  missing_code: 'Sign-in was interrupted. Please try again.',
  oauth_failed: 'Sign-in failed. Please try again.',
};

export default function Login() {
  const [params] = useSearchParams();
  const errorCode = params.get('error');
  const errorMessage = errorCode ? (ERROR_MESSAGES[errorCode] || 'Sign-in failed. Please try again.') : null;

  const handleLogin = () => {
    // Full-page navigation — the server handles the OAuth redirect + callback.
    window.location.href = '/api/auth/google';
  };

  return (
    <div className="login-page">
      <div className="brand-panel">
        <img src="/logo-light.svg" alt="Front Porch Solutions" />
      </div>
      <div className="right-panel">
        <div className="login-content">
          <h1 className="login-app-title">FPS Maps</h1>
          <p className="login-prompt">
            Build custom territory and targeting maps from ZIPs, cities, counties, and radii.
            <br />
            Sign in with your <strong>@frontporchsolutions.com</strong> account to continue.
          </p>
          <hr className="login-divider" />
          {errorMessage && (
            <div role="alert" className="login-error">{errorMessage}</div>
          )}
          <button className="google-signin-btn" onClick={handleLogin}>
            <span className="google-icon-pill">
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
                <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" />
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
              </svg>
            </span>
            Sign in with Google
          </button>
          <p className="login-footer-note">Access restricted to Front Porch Solutions team members.</p>
        </div>
        <div className="login-page-footer">
          <div>Made with ♥ by Front Porch Solutions</div>
          {/* Privacy/Terms links per mockups/01-login.html:156. Placeholder "#"
              hrefs — no destination pages exist yet; the mock-up uses the same
              placeholders. This is what the .login-page-footer a / .sep styles
              in Login.css are for. anchor-is-valid is disabled here because these
              are intentional placeholders; swap "#" for real routes when the
              Privacy/Terms pages ship. */}
          <div>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#">Privacy Policy</a>
            <span className="sep">·</span>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#">Terms of Service</a>
          </div>
        </div>
      </div>
    </div>
  );
}
